import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * MUI Icons
 */
import WarningIcon from '@mui/icons-material/Warning';

/**
 * Components
 */
import WorkRowCard from './WorkRowCard';
import { Box, Typography, Button, Chip } from '@mui/material';

const styles = {
    workList: {
        display: "flex",
        flexDirection: "column",
        gap: "20px",
    },
};

const ListWorks = ({ items, lockNoSameDay = true }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [filter, setFilter] = useState('all'); // all | today | future | unfilled
    const [sortedItems, setSortedItems] = useState([]);

    const handleRefreshPage = (event) => {
        event.preventDefault();
        navigate('/');
        window.location.reload();
    };

    if (!Array.isArray(items) || items.length < 1) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <WarningIcon color='warning' sx={{ mb: 2, fontSize: 50 }} />
                <Typography variant="h6">{t('noResults')}</Typography>
                <Typography color={'text.secondary'} variant="body1">{t('noPlanningsFoundMessage')}</Typography>
                <Button variant="contained" sx={{ mt: 2 }} onClick={handleRefreshPage}>{t('Refresh')}</Button>
            </Box>
        );
    }

    const lockWork = (planning) => {
        if (lockNoSameDay) {
            const today = new Date().toISOString().split('T')[0];   // 'YYYY-MM-DD'
            const planningDate = planning.date.slice(0, 10);        // 'YYYY-MM-DD'

            // If the planning is older than today do not lock it.
            if (planningDate < today) {
                return false;
            }

            // If the planning is not today, lock it.
            if (planningDate !== today) {
                return true;
            }
        }
        return false;
    };

    // Order items by date and block them as:
    // - Plannings from today should be first
    // - Plannings from the future should be in the middle
    // - Plannings from the past should be last
    useEffect(() => {
        const today = new Date().toISOString().split('T')[0];

        const ordered = [...items].sort((a, b) => {
            const dateA = a.date.slice(0, 10);
            const dateB = b.date.slice(0, 10);

            const isTodayA = dateA === today;
            const isTodayB = dateB === today;

            const isFutureA = dateA > today;
            const isFutureB = dateB > today;

            if (isTodayA && !isTodayB) return -1;
            if (!isTodayA && isTodayB) return 1;

            if (isFutureA && !isFutureB) return -1;
            if (!isFutureA && isFutureB) return 1;

            return dateA.localeCompare(dateB);
        });

        setSortedItems(ordered);
    }, [items]);

    const filteredItems = sortedItems.filter(item => {
        const date = item.date.slice(0, 10);
        const today = new Date().toISOString().split('T')[0];
        const hasReport = item.has_report === true;

        if (filter === 'today') return date === today;
        if (filter === 'future') return date > today;
        if (filter === 'unfilled') return date < today && !hasReport;

        return true; // all
    });

    const StatsBadges = () => {
        const today = new Date().toISOString().split('T')[0];

        let todayTotal = 0;
        let todayFulfilled = 0;
        let futureTotal = 0;
        let unfilledPast = 0;

        for (const item of items) {
            const date = item.date.slice(0, 10);
            const hasReport = item.has_report === true;

            if (date === today) {
                todayTotal++;
                if (hasReport) todayFulfilled++;
            } else if (date > today) {
                futureTotal++;
            } else {
                if (!hasReport) unfilledPast++;
            }
        }

        return (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Button
                    variant={filter === 'all' ? 'contained' : 'outlined'}
                    onClick={() => setFilter('all')}
                >
                    {t('all')}
                </Button>
                <Button
                    variant={filter === 'today' ? 'contained' : 'outlined'}
                    onClick={() => setFilter('today')}
                >
                    {t('today')}: {todayFulfilled}/{todayTotal}
                </Button>
                <Button
                    variant={filter === 'future' ? 'contained' : 'outlined'}
                    onClick={() => setFilter('future')}
                >
                    {t('upcoming')}: {futureTotal}
                </Button>
                <Button
                    variant={filter === 'unfilled' ? 'contained' : 'outlined'}
                    onClick={() => setFilter('unfilled')}
                >
                    {t('unfilledPast')}: {unfilledPast}
                </Button>
            </Box>
        );
    };

    return (
        <>
            <StatsBadges />
            <div style={styles.workList}>
                {filteredItems.map(item => (
                    <WorkRowCard key={item.id} planning={item} lock={lockWork(item)} />
                ))}
            </div>
        </>
    );
};

export default ListWorks;